import { useState } from "react";
import { useNavigate } from "react-router-dom";

import AppShell from "@/components/AppShell";
import LogModeTabs from "@/components/LogModeTabs";
import ReadingForm from "@/components/ReadingForm";
import { api } from "@/lib/api";
import { useAsync, useVesselPath } from "@/lib/hooks";
import type { ParamKey } from "@/lib/params";
import {
  type ReadingDraft,
  blankDraft,
  buildCreatePayload,
  draftHasContent,
} from "@/lib/readingDraft";
import type { Reading ,
  Vessel,
} from "@/types";

export default function LogReading() {
  const vp = useVesselPath();
  // Only to decide which fields are worth asking for; a failure here must not
  // stop you logging a reading, so it degrades to showing everything.
  const vessel = useAsync<Vessel>(() => api.get<Vessel>(""), []);
  const navigate = useNavigate();
  // "Now" is captured once, at open — not re-read on every render.
  const [initial] = useState(blankDraft);

  async function create(draft: ReadingDraft, shown: ParamKey[]) {
    const created = await api.post<Reading>("/api/readings", buildCreatePayload(draft, shown));
    navigate(vp(`/history/${created.id}`), { replace: true });
  }

  return (
    <AppShell title="Log" subtitle="Leave anything you didn't test blank.">
      <LogModeTabs />
      <ReadingForm
        vesselType={vessel.data?.vessel_type}
        initial={initial}
        submitLabel="Save reading"
        pendingLabel="Saving…"
        errorFallback="Could not save the reading."
        blockedReason={(draft, shown) =>
          draftHasContent(draft, shown) ? null : "Enter at least one measurement or a note."
        }
        onSubmit={create}
      />
    </AppShell>
  );
}
